import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import LoadingButton from '@/components/ui/LoadingButton';
import { Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createWithdraw } from '@/shared/store/slices/walletSlice';
import Axios from '@/shared/api/axios';

const WithdrawConfirmModal = forwardRef(({ onSuccess, onError }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentIdolId = useSelector(state => state.user.idolId);

  const [show, setShow] = useState(false);
  const [withdrawData, setWithdrawData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useImperativeHandle(ref, () => ({
    show: (data) => {
      setWithdrawData(data);
      setVerificationCode('');
      setResendCountdown(0);
      setShow(true);
    },
    hide: () => {
      setShow(false);
    }
  }));

  const handleClose = () => {
    setShow(false);
    setWithdrawData(null);
    setVerificationCode('');
    setResendCountdown(0);
  };

  // 倒計時功能
  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  // 發送驗證碼
  const handleSendVerification = async () => {
    if (resendCountdown > 0 || isResending) return;

    setIsResending(true);

    try {
      const response = await Axios.post('/wallet/withdraw/send-verification', {}, {
        params: { idol_id: currentIdolId }
      });

      if (response.data?.status_code === 'SYSTEM_1000') {
        setResendCountdown(60);
      } else {
        // 錯誤處理
        if (onError) {
          onError(response.data?.status_code);
        }
      }
    } catch (error) {
      console.error('發送驗證碼失敗:', error);
      if (onError && error.httpErrorCode) {
        onError(error.httpErrorCode);
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleConfirm = async () => {
    if (!withdrawData || !verificationCode.trim()) return;

    setIsSubmitting(true);

    try {
      await dispatch(createWithdraw({
        idolId: currentIdolId,
        amount: withdrawData.amount,
        wallet_address: withdrawData.walletAddress,
        code: verificationCode
      })).unwrap();

      // 成功後關閉 modal 並顯示成功訊息
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (statusCode) {
      // 錯誤由 WalletPage 的 StatusModal 處理
      if (onError) {
        onError(statusCode);
      }
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  // 驗證碼驗證
  const isValid = verificationCode.trim().length > 0;

  if (!withdrawData) return null;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
    >
      <Modal.Header className="border-bottom pb-3" closeButton={!isSubmitting}>
        <Modal.Title className="fw-bold">{t('withdraw_confirmation')}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="py-4">
        {/* 網路類型 */}
        <div className="mb-4">
          <h6 className="fw-bold mb-2">{t('network_type')}</h6>
          <p className="mb-0">ETH (ERC20)</p>
        </div>

        {/* 收款錢包地址 */}
        <div className="mb-4">
          <h6 className="fw-bold mb-2">{t('wallet_address')}</h6>
          <p className="mb-0 text-break">{withdrawData.walletAddress}</p>
        </div>

        {/* 實際到帳 */}
        <div className="mb-4">
          <h6 className="fw-bold mb-2">{t('actual_amount')}</h6>
          <p className="mb-0">
            <span className="fs-5 fw-bold text-primary">
              ₣{withdrawData.actualAmount} FCoin
            </span>
            {' '}
            <span className="text-gray ps-1">
              ({t('fee')}{withdrawData.fee} USDT)
            </span>
          </p>
        </div>

        <hr />

        {/* 安全驗證 */}
        <div>
          <h6 className="fw-bold mb-3">{t('security_verification')}</h6>

          <div className="d-flex gap-2 mb-3">
            <LoadingButton
              className="btn btn-primaryF text-nowrap flex-shrink-0"
              style={{ minWidth: '210px' }}
              onClick={handleSendVerification}
              disabled={resendCountdown > 0 || isSubmitting}
              isLoading={isResending}
            >
              {resendCountdown > 0
                ? t('resend_in_seconds', { seconds: resendCountdown })
                : t('send_verification_letter')}
            </LoadingButton>
            <Form.Control
              type="text"
              placeholder={t('verification_code')}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              style={{ flex: 1 }}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 d-flex justify-content-end gap-2">
        <button
          className="btn btn-neutral rounded-pill"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          {t('cancel')}
        </button>
        <LoadingButton
          className="btn btn-primaryF rounded-pill"
          onClick={handleConfirm}
          disabled={!isValid}
          isLoading={isSubmitting}
        >
          {t('confirm')}
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
});

WithdrawConfirmModal.displayName = 'WithdrawConfirmModal';

export default WithdrawConfirmModal;
